import { AgTechDataAction } from "@agtech/core/data/actions/AgTechDataActions";
import { useAgTechGetRequest } from "@agtech/core/data/http/AgTechApiGetRequests";
import { useAgTechHttpPostEntityRequest, useAgTechHttpPostRequest } from "@agtech/core/data/http/AgTechApiPostRequests";
import { SaleBidder } from "../../bidders/BidderData";
import { Index } from "@agtech/core/utilities/AgTechUtilities";
import { AgTechContact } from "@agtech/data/contacts/ContactData";
import { ContactDataGetAllContactsQuery } from "@agtech/data/contacts/ContactDataQueries";

/// Contact Data Actions

export const useSaleContactDataWriteContactAction = (): AgTechDataAction<AgTechContact, AgTechContact> => {
    const writeContactSubmissionRequest = useAgTechHttpPostEntityRequest<AgTechContact>();

    return {
        name: 'WriteContact',
        getConfiguration: props => ({
            actionExecutionMessage: props.originalEntity?.id ? 'Updating contact...' : 'Creating contact...',
            actionConfirmationMessage: `Successfully ${props.originalEntity?.id ? 'updated' : 'created'} contact`
        }),
        action: async props => {
            return await writeContactSubmissionRequest.post({
                path: 'Contact/Submit',
                postedData: props.submittedEntity
            });
        },
        onSuccess: async props => {
            props.updateQueryData({
                query: ContactDataGetAllContactsQuery,
                update: (contacts) => {
                    return {
                        ...contacts,
                        [props.responseData.id]: props.responseData
                    }
                }
            });
        }
    }
}

export const useContactDataDeleteContactAction = (): AgTechDataAction<AgTechContact, boolean> => {
    let saleBiddersGetRequest = useAgTechGetRequest<SaleBidder[]>();
    let contactDeletionHttpRequest = useAgTechHttpPostRequest<AgTechContact, boolean>();

    // TODO: Validations

    return {
        name: 'DeleteContact',
        getConfiguration: () => ({
            actionExecutionMessage: 'Deleting contact...',
            actionConfirmationMessage: `Successfully deleted contact`
        }),
        validate: async props  => {
            let bidderRegistrationsForContactResponse = await saleBiddersGetRequest.get({
                path: 'Bidder/BidderRegistrations/' + props.submittedEntity.id
            });

            props.validation.failWithWarningIf(
                (bidderRegistrationsForContactResponse.success?.data?.length ?? 0) > 0,
                {
                    header: `${props.submittedEntity.firstName} ${props.submittedEntity.lastName} has active bidder registrations and cannot be deleted.`,
                    details: 'Please remove all bidder registrations for this contact before deleting.'
                }
            )
        },
        confirm: async props => {
            let contactFullName = `${props.submittedEntity.firstName} ${props.submittedEntity.lastName}`;
            
            return await props.appContext.confirmations.askForConfirmation({
                content: {
                    header: `Are you sure you want to remove "${contactFullName}" from your contacts?`,
                    details: `All notes and credits for this contact will be deleted as well.`,
                    confirmationButtonText: 'Delete Contact',
                    width: '480px'
                }
            });
        },
        action: async props => {
            return await contactDeletionHttpRequest.post({
                path: 'Contact/Delete',
                postedData: props.submittedEntity,
            });
        },
        onSuccess: async props => {
            props.updateQueryData({
                query: ContactDataGetAllContactsQuery,
                update: contacts => {
                    const updatedContacts = Object.values(contacts)
                        .filter(c => c.id !== props.submittedEntity.id);

                    return Index(updatedContacts, c => c.id);
                }
            });

            props.appContext.popups.closeAll();
        }
    }
}